import { cn } from '@/lib/utils'
import { selectedCellsSelector, setSelectedCells } from '@/redux/store/slices/map/mapSlice'
import { themeSelector } from '@/redux/store/slices/theme/themeSlice'
import { Theme } from '@/theme'
import { useAppDispatch, useAppSelector } from '@redux/hooks/useAppState'
import { useCallback, useRef } from 'react'
import Map from 'react-map-gl/mapbox'

import type { FC, PropsWithChildren } from 'react'
import type { MapRef, ViewState } from 'react-map-gl/mapbox'

import 'mapbox-gl/dist/mapbox-gl.css'

interface MapViewProps extends PropsWithChildren {
	viewState?: ViewState
	onMapMoved?: (evt: { viewState: ViewState }) => void
}

const INITIAL_VIEW_STATE = {
	latitude: 37.7577,
	longitude: -122.4376,
	zoom: 8,
	pitch: 0,
	bearing: 0,
	padding: { top: 170, bottom: 0, left: 0, right: 0 },
}

export const MapView: FC<MapViewProps> = ({ children, viewState, onMapMoved }) => {
	const mapRef = useRef<MapRef>(null)
	const theme = useAppSelector(themeSelector)
	const dispatch = useAppDispatch()
	const selectedCellsList = useAppSelector(selectedCellsSelector)

	const handleResize = useCallback(() => {
		mapRef.current?.resize()
	}, [])

	const handleCellClick = (cellId: string) => {
		const currentCells: string[] = selectedCellsList || []
		dispatch(setSelectedCells([...currentCells, cellId]))
	}

	const handleBoundsChange = () => {
		const map = mapRef.current
		if (!map) return

		const bounds = map.getBounds()
		if (!bounds) return

		// Handle bounds update
	}

	// Ensure padding is always applied
	const effectiveViewState = {
		...(viewState || INITIAL_VIEW_STATE),
		padding: viewState?.padding || INITIAL_VIEW_STATE.padding,
	}

	return (
		<div className={cn('h-full w-full', theme === Theme.Dark ? 'bg-[#242424]' : 'bg-background')}>
			<Map
				ref={mapRef}
				{...effectiveViewState}
				onMove={onMapMoved}
				style={{ width: '100%', height: '100%' }}
				mapStyle="mapbox://styles/mapbox/streets-v11"
				onMoveEnd={handleBoundsChange}
				onClick={(e) => handleCellClick(e.features?.[0]?.id as string)}
				onResize={handleResize}
				mapboxAccessToken={import.meta.env.VITE_MAPBOX_ACCESS_TOKEN}
			>
				{children}
			</Map>
		</div>
	)
}
